import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import type { AgendaEvent } from "@domaddress/shared";
import EventRosterPlanner from "../components/EventRosterPlanner";
import EventRoomPlanner from "../components/EventRoomPlanner";
import { describeRecurrence, isRecurring } from "../lib/recurrenceLabel";

const WP_ADMIN_BASE =
  import.meta.env.VITE_WORDPRESS_ADMIN_URL ?? "https://dominicusamsterdam.nl/wp-admin";

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<AgendaEvent | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get<{ data: AgendaEvent }>(`/api/events/${id}`);
      setEvent(res.data.data);
    } catch {
      setError("Evenement niet gevonden of geen toegang.");
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="page">
        <p>Evenement laden…</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="page">
        <p className="form-error">{error || "Onbekende fout"}</p>
        <Link to="/evenementen">← Terug naar evenementen</Link>
      </div>
    );
  }

  const wpEditUrl = event.wp_post_id
    ? `${WP_ADMIN_BASE}/post.php?post=${event.wp_post_id}&action=edit`
    : null;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <Link to="/evenementen" className="back-link">
            ← Evenementen
          </Link>
          <h1 className="page-title">
            {event.title}
            {isRecurring(event) && (
              <span className="event-recurrence-badge event-recurrence-badge-title" title="Terugkerend">
                ↻
              </span>
            )}
          </h1>
          <p className="page-subtitle">
            {event.start_datetime}
            {event.end_datetime ? ` — ${event.end_datetime}` : ""}
            {event.location_name ? ` · ${event.location_name}` : ""}
          </p>
          {event.wp_post_id && (
            <p className="page-subtitle">WordPress domevent #{event.wp_post_id}</p>
          )}
        </div>
      </header>

      <div className="card event-recurrence-card">
        <h2>Herhaling</h2>
        <p>{describeRecurrence(event)}</p>
        {wpEditUrl && (
          <p className="page-subtitle" style={{ marginBottom: 0 }}>
            Herhaling bewerken in{" "}
            <a href={wpEditUrl} target="_blank" rel="noreferrer">
              WordPress (dom_event)
            </a>
            .
          </p>
        )}
        {!wpEditUrl && isRecurring(event) && (
          <p className="page-subtitle" style={{ marginBottom: 0 }}>
            Dit evenement komt uit WordPress-sync; herhaling wordt daar beheerd.
          </p>
        )}
      </div>

      <EventRoomPlanner event={event} onEventUpdated={setEvent} />
      <EventRosterPlanner event={event} onEventUpdated={setEvent} />
    </div>
  );
}
